import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { FormattedMoney } from '../atomic-components';
import { InformationIcon } from '../icons/information-icon';
import { CashFlowCalendarEntry } from '@algoan/dashboard/model';
import { roundDecimals } from '@algoan/dashboard/utils';
import { useFormatMoney } from '../../utils/use-format-money';

interface MonthlyAmountCashFlowProps {
  calendarEntry: CashFlowCalendarEntry;
}

enum RejectedPayementsStatus {
  ALL_TRANSACTIONS_REJECTED,
  SOME_TRANSACTIONS_REJECTED,
  NO_TRANSACTIONS_REJECTED,
}

const computeRejectedPayments = (totalAmountRejectedAmount: number, totalAmount: number): RejectedPayementsStatus => {
  const rejectedAmount = roundDecimals(totalAmountRejectedAmount);
  if (rejectedAmount === 0) return RejectedPayementsStatus.NO_TRANSACTIONS_REJECTED;
  if (rejectedAmount === totalAmount) return RejectedPayementsStatus.ALL_TRANSACTIONS_REJECTED;
  else return RejectedPayementsStatus.SOME_TRANSACTIONS_REJECTED;
};

export const MonthlyAmountCashFlow: React.FC<MonthlyAmountCashFlowProps> = ({ calendarEntry }) => {
  const intl = useIntl();
  const formatMoney = useFormatMoney();

  const computeRejectedPaymentsCase = computeRejectedPayments(calendarEntry.totalRejectedAmount, calendarEntry.amount);

  switch (computeRejectedPaymentsCase) {
    case RejectedPayementsStatus.ALL_TRANSACTIONS_REJECTED:
      return (
        <S.RejectedBlock data-testid="rejected-cash-flow">
          <S.Amount isRejected={calendarEntry.totalRejectedAmount > 0}>
            <FormattedMoney number={Math.abs(calendarEntry.amount || 0)} />
          </S.Amount>
        </S.RejectedBlock>
      );

    case RejectedPayementsStatus.SOME_TRANSACTIONS_REJECTED:
      return (
        <S.IconBlock data-testid="rejected-cash-flow-icon">
          <S.RejectedBlock
            data-tooltip-id="cash_flow.rejected_payments.tooltip"
            data-tooltip-content={intl.formatMessage(
              {
                id: 'tooltip_rejected_payments.message',
                description: 'Title of the tooltip',
                defaultMessage: '{rejectedAmount} were rejected out of {totalAmount}.',
              },
              {
                rejectedAmount: formatMoney(calendarEntry.totalRejectedAmount),

                totalAmount: formatMoney(calendarEntry.amount),
              },
            )}
          >
            <InformationIcon />
          </S.RejectedBlock>
          <S.RejectedBlock>
            <S.Exclamation>!</S.Exclamation>&nbsp;
            <FormattedMoney number={calendarEntry.amount} />
          </S.RejectedBlock>
        </S.IconBlock>
      );
    case RejectedPayementsStatus.NO_TRANSACTIONS_REJECTED:
      return <FormattedMoney number={calendarEntry.amount} />;
  }
};

const S = {
  RejectedBlock: styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
  `,
  IconBlock: styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding-left: 4px;
  `,
  Amount: styled.div<{ isRejected: boolean }>`
    color: ${(props) => (props.isRejected ? props.theme.redNotification : props.theme.algoanBlue)};
  `,

  Exclamation: styled.div`
    font-weight: 900;
    color: ${(props) => props.theme.redNotification};
  `,
};
